import BaseAPI from './base.api';
import { type ImportedLocation } from './location-importer.api';
import { type GeoJSONLineString } from 'utils/geohash';

export interface WidthSegment {
  from: string;
  to: string;
  width: number;
}

export interface HaulRoute {
  routeId: string;
  customerId: string;
  fromLocationId: string;
  toLocationId: string;
  routeKey: string;
  roadName?: string | null;
  routeVariant: string | null;
  geohashes: string[];
  minutes: number | null;
  active: boolean;
  distanceMeters: number | null;
  surfaceType: string | null;
  speedLimitMinKph?: number | null;
  speedLimitMaxKph?: number | null;
  numberOfLanes?: number | null;
  roadCondition?: string | null;
  geoEngineerSuggestions?: string | null;
  widthSegments?: WidthSegment[] | null;
  truckCapacity?: number | null;
  weightCapacityInTon?: number | null;
  pathGeog?: GeoJSONLineString | null;
  createdAt: string;
  updatedAt: string;
  fromLocation?: ImportedLocation;
  toLocation?: ImportedLocation;
}

export interface RouteDistancePathSegment {
  routeId: string;
  roadName: string | null;
  fromLocationId: string;
  fromLocationName: string;
  toLocationId: string;
  toLocationName: string;
  distanceMeters: number;
}

export interface RouteDistancePathOption {
  totalDistanceMeters: number;
  locationIds: string[];
  locationNames: string[];
  segments: RouteDistancePathSegment[];
}

export default class HaulRouteAPI extends BaseAPI {
  getRoutes = async (): Promise<HaulRoute[]> => {
    const response = await this.API.get('haul-route', {
      params: { customerId: this.customerId },
    });
    return response.data;
  };

  getRoutePaths = async (
    fromLocationId: string,
    toLocationId: string,
    options?: { via?: string[]; all?: boolean }
  ): Promise<RouteDistancePathOption[]> => {
    const response = await this.API.get('haul-route/paths', {
      params: {
        customerId: this.customerId,
        fromLocationId,
        toLocationId,
        via: options?.via,
        all: options?.all,
      },
    });
    return response.data;
  };

  deleteRoute = async (routeId: string): Promise<void> => {
    await this.API.delete(`haul-route/${routeId}`);
  };

  updateRoute = async (
    routeId: string,
    patch: Record<string, unknown>
  ): Promise<void> => {
    await this.API.patch(`haul-route/${routeId}`, patch);
  };

  updateGeohashes = async (
    routeId: string,
    geohashes: string[],
    path?: GeoJSONLineString | null
  ): Promise<void> => {
    await this.API.put(`haul-route/${routeId}/geohashes`, { geohashes, path });
  };

  setRouteActive = async (routeId: string, active: boolean): Promise<void> => {
    await this.API.patch(`haul-route/${routeId}`, { active });
  };

  createRoute = async (payload: {
    fromLocationName: string;
    toLocationName: string;
    roadName?: string | null;
    geohashes: string[];
    minutes: number;
    routeVariant: number | null;
    truckCapacity?: number | null;
    weightCapacityInTon?: number | null;
    distanceMeters?: number;
    speedLimitMinKph?: number;
    speedLimitMaxKph?: number;
    numberOfLanes?: number;
    roadCondition?: string;
    geoEngineerSuggestions?: string;
    widthSegments?: WidthSegment[];
    path?: GeoJSONLineString | null;
  }): Promise<void> => {
    await this.API.post('haul-route', {
      customerId: this.customerId,
      ...payload,
    });
  };
}
